import { z } from 'zod';

import {
  ActivityType,
  Civility,
  KycStatus,
  OppSource,
  Role,
  Stage,
  WealthSource,
  WealthTranche,
} from '../enums/index.js';

const enumValues = <T extends Record<string, string>>(o: T) =>
  Object.values(o) as [string, ...string[]];

export const OpportunityCreateSchema = z.object({
  name: z.string().min(1).max(200),
  passport_origin: z.string().max(120).nullable().optional(),
  // Free text up to 60 chars — accepts both ISO codes ("FR") and the legacy
  // Excel descriptors ("UK RES NON DOM", "SWISS - Basel").
  domiciled_country: z.string().min(1).max(60),
  industry_sector: z.string().min(1).max(60),
  total_wealth: z.number().nonnegative(),
  initial_expected_amount: z.number().nonnegative(),
  potential_expected_amount_y1: z.number().nonnegative().default(0),
  potential_negotiation: z.boolean().default(false),
  roa_bps: z.number().min(0).max(500).nullable().optional(),
  expected_timeframe: z.string().max(40).nullable().optional(),
  existing_bank: z.string().max(60).nullable().optional(),
  target_bank_id: z.string().uuid().nullable().optional(),
  source: z.enum(enumValues(OppSource)),
  referred_by: z.string().max(200).optional(),
  pep_flag: z.boolean().default(false),
});
export type OpportunityCreateInput = z.infer<typeof OpportunityCreateSchema>;

export const OpportunityUpdateSchema = OpportunityCreateSchema.partial().extend({
  stage_probability_override: z.number().min(0).max(100).nullable().optional(),
  stage_probability_override_reason: z.string().min(20).nullable().optional(),
  reverse_solicitation_evidence: z.string().nullable().optional(),
  kyc_status: z.enum(enumValues(KycStatus)).optional(),
  kyc_last_review_date: z.string().date().nullable().optional(),
});
export type OpportunityUpdateInput = z.infer<typeof OpportunityUpdateSchema>;

/**
 * Inline-edit payload for the pipeline table AND the detail-page edit form.
 * Covers every field the user can change without going through a dedicated
 * transition route. The STATUS (N) column still goes through /:id/stage so
 * the KYC compliance gate runs.
 *
 * NOT editable here on purpose:
 *   - stage (must transition via /:id/stage to keep the audit + KYC gate)
 *   - contact_id (referential integrity — change via a separate endpoint
 *     if and when that need arises)
 *   - owner_rm_id (handled by a dedicated re-assignment flow)
 *   - score_chf / stage_probability_pct (computed)
 */
export const OpportunityInlineEditSchema = z
  .object({
    name: z.string().min(1).max(200),
    passport_origin: z.string().max(120).nullable(),
    domiciled_country: z.string().min(1).max(60),
    industry_sector: z.string().min(1).max(60),
    total_wealth: z.number().nonnegative(),
    initial_expected_amount: z.number().nonnegative(),
    roa_bps: z.number().min(0).max(500).nullable(),
    potential_expected_amount_y1: z.number().nonnegative(),
    potential_negotiation: z.boolean(),
    expected_timeframe: z.string().max(40).nullable(),
    existing_bank: z.string().max(60).nullable(),
    target_bank_id: z.string().uuid().nullable(),
    source: z.enum(enumValues(OppSource)),
    referred_by: z.string().max(200).nullable(),
    pep_flag: z.boolean(),
    kyc_status: z.enum(enumValues(KycStatus)),
    kyc_last_review_date: z.string().date().nullable(),
    reverse_solicitation_evidence: z.string().max(2000).nullable(),
  })
  .partial();
export type OpportunityInlineEditInput = z.infer<typeof OpportunityInlineEditSchema>;

export const StageTransitionSchema = z.object({
  to_stage: z.enum(enumValues(Stage)),
  override_probability: z.number().min(0).max(100).nullable().optional(),
  override_reason: z.string().min(20).nullable().optional(),
});
export type StageTransitionInput = z.infer<typeof StageTransitionSchema>;

export const ActivityCreateSchema = z.object({
  type: z.enum(enumValues(ActivityType)),
  activity_date: z.string().datetime(),
  duration_minutes: z.number().int().nonnegative().optional(),
  counterparty: z.string().max(200).optional(),
  summary: z.string().max(2000).optional(),
});
export type ActivityCreateInput = z.infer<typeof ActivityCreateSchema>;

export const UserCreateSchema = z.object({
  email: z.string().email(),
  display_name: z.string().min(1).max(120),
  role: z.enum(enumValues(Role)),
  preferred_locale: z.enum(['fr', 'en']).default('fr'),
});
export type UserCreateInput = z.infer<typeof UserCreateSchema>;

// ---------------------------------------------------------------------------
// CONTACTS (prospects directory)
// ---------------------------------------------------------------------------

const Iso2 = z
  .string()
  .length(2)
  .transform((s) => s.toUpperCase());

export const ContactAddressSchema = z.object({
  label: z.string().min(1).max(40),
  street: z.string().max(200).nullable().optional(),
  postal_code: z.string().max(20).nullable().optional(),
  city: z.string().max(120).nullable().optional(),
  country: Iso2.nullable().optional(),
  is_primary: z.boolean().default(false),
});
export type ContactAddressInput = z.infer<typeof ContactAddressSchema>;

export const ContactCreateSchema = z.object({
  civility: z.enum(enumValues(Civility)).nullable().optional(),
  first_name: z.string().min(1).max(80),
  last_name: z.string().min(1).max(80),
  nickname: z.string().max(80).nullable().optional(),
  photo_url: z.string().url().max(500).nullable().optional(),

  email_primary: z.string().email().nullable().optional(),
  email_secondary: z.string().email().nullable().optional(),
  phone_primary: z.string().max(40).nullable().optional(),
  phone_secondary: z.string().max(40).nullable().optional(),
  linkedin_url: z.string().url().max(255).nullable().optional(),

  company: z.string().max(200).nullable().optional(),
  position: z.string().max(120).nullable().optional(),
  industry_sector: z.string().max(60).nullable().optional(),

  date_of_birth: z.string().date().nullable().optional(),
  nationalities: z.array(Iso2).max(10).default([]),
  tax_residence: Iso2.nullable().optional(),
  languages: z.array(z.string().length(2)).max(10).default([]),

  wealth_tranche: z.enum(enumValues(WealthTranche)).default(WealthTranche.UNKNOWN),
  wealth_source: z.enum(enumValues(WealthSource)).default(WealthSource.UNKNOWN),

  pep_flag: z.boolean().default(false),
  id_document_type: z.string().max(40).nullable().optional(),
  id_document_masked: z.string().max(40).nullable().optional(),
  id_document_expiry: z.string().date().nullable().optional(),
  kyc_last_review_date: z.string().date().nullable().optional(),
  kyc_status: z.enum(enumValues(KycStatus)).default(KycStatus.PENDING),

  source: z.enum(enumValues(OppSource)).default(OppSource.OTHER),
  referred_by_contact: z.string().uuid().nullable().optional(),
  first_met_at: z.string().date().nullable().optional(),
  owner_rm_id: z.string().uuid().nullable().optional(),

  tags: z.array(z.string().max(40)).max(30).default([]),
  notes: z.string().max(8000).nullable().optional(),

  addresses: z.array(ContactAddressSchema).max(10).default([]),
});
export type ContactCreateInput = z.infer<typeof ContactCreateSchema>;

export const ContactUpdateSchema = ContactCreateSchema.partial();
export type ContactUpdateInput = z.infer<typeof ContactUpdateSchema>;

/**
 * Business card OCR endpoint payload — the API receives a base64 image,
 * runs vision OCR, and returns suggested fields for the user to confirm.
 * Image bytes are never stored; only the SHA-256 hash for audit (RGPD/nLPD).
 */
export const ContactScanRequestSchema = z.object({
  image_base64: z.string().min(50, 'image_too_small').max(15_000_000, 'image_too_large'), // ~10 MiB base64
  mime_type: z.enum(['image/jpeg', 'image/png', 'image/webp']).default('image/jpeg'),
});
export type ContactScanRequest = z.infer<typeof ContactScanRequestSchema>;

/**
 * Result shape produced by Claude Vision via `tool_use` (extract_business_card
 * tool). The schema is intentionally richer than the previous flat shape so
 * the frontend can:
 *   - keep ALL emails / phones (not just primary/secondary)
 *   - distinguish phone types (mobile/office/fax) using OCR labels
 *   - display a confidence badge in the preview screen
 *   - parse structured address components for both EU and US formats
 *
 * The backend normalization layer fills `phones[i].e164` whenever possible,
 * lowercases emails, and forces `country` to ISO 3166-1 alpha-2.
 */
// Scalar that may be (a) absent from the model output, (b) explicitly null,
// or (c) a string. The tool's `required` array intentionally only lists
// emails/phones/confidence so the model can OMIT keys that aren't on the
// card. We normalise undefined → null at parse time so downstream code only
// ever sees `string | null`.
const nullableScalar = z
  .string()
  .nullable()
  .optional()
  .transform((v) => v ?? null);

export const ContactScanPhoneSchema = z.object({
  raw: z.string(),
  type: z.enum(['mobile', 'office', 'fax', 'unknown']),
  e164: nullableScalar,
});
export type ContactScanPhone = z.infer<typeof ContactScanPhoneSchema>;

export const ContactScanConfidenceSchema = z.object({
  overall: z.enum(['high', 'medium', 'low']),
  notes: z.string().default(''),
});
export type ContactScanConfidence = z.infer<typeof ContactScanConfidenceSchema>;

export const ContactScanResultSchema = z.object({
  firstName: nullableScalar,
  lastName: nullableScalar,
  title: nullableScalar,
  company: nullableScalar,
  department: nullableScalar,
  emails: z.array(z.string()).default([]),
  phones: z.array(ContactScanPhoneSchema).default([]),
  website: nullableScalar,
  linkedinUrl: nullableScalar,
  addressLine: nullableScalar,
  postalCode: nullableScalar,
  city: nullableScalar,
  stateOrRegion: nullableScalar,
  country: nullableScalar,
  confidence: ContactScanConfidenceSchema,
});
export type ContactScanResult = z.infer<typeof ContactScanResultSchema>;

// ---------------------------------------------------------------------------
// OPPORTUNITY ↔ CONTACT
// ---------------------------------------------------------------------------
export const OpportunityFromContactSchema = OpportunityCreateSchema.extend({
  contact_id: z.string().uuid(),
});
export type OpportunityFromContactInput = z.infer<typeof OpportunityFromContactSchema>;
